import React, { FC, useRef, useImperativeHandle, useCallback } from 'react';
import styled from 'styled-components';
import TextField, { TextFieldProps } from '@cld/text-field';
import { asStyledComponent } from '@cld/component-enhancer';
import NumericDecorator from './components/NumericDecorator';

export interface NumberFieldProps extends Omit<TextFieldProps, 'type'> {
  /** The maximum value to accept for this input */
  max?: number;
  /** The minimum value to accept for this input */
  min?: number;
  /** A stepping interval to use when using up and down arrows to adjust the value, as well as for validation **/
  step?: number;
  /** Display numericDecorator icon **/
  numericDecorator?: boolean;
}

const NumericTextField = styled(TextField)<NumberFieldProps>`
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type=number] {
    -moz-appearance: textfield;
  }
`;

export const NumberField: FC<NumberFieldProps> = ({
                                             inputRef,
                                             trailingDecoration,
                                             min,
                                             max,
                                             step,
                                             isReadonly,
                                             isDisabled,
                                             inputProps,
                                             numericDecorator = true,
                                             ...restProps
                                           }: NumberFieldProps) => {
  const internalRef = useRef<HTMLInputElement>(null);

  // useImperativeHandle makes it possible to keep NumberField both controlled and uncontrolled
  // if inputRef prop is passed, it will get the ref from TextField
  // internalRef is used for the up/down buttons to control the value from the DOM and not force the NumberField into controlled mode
  // it supports both function and object {current: ...}
  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
    inputRef, () => internalRef.current, []
  );

  const dispatchEvent = (element?: HTMLInputElement | null) => {
    const event = new Event('input', { bubbles: true });
    element?.dispatchEvent(event);
  };

  const onUpKey = useCallback(() => {
    internalRef.current?.stepUp();
    dispatchEvent(internalRef.current);
  }, [internalRef]);

  const onDownKey = useCallback(() => {
    internalRef.current?.stepDown();
    dispatchEvent(internalRef.current);
  }, [internalRef]);

  return (
    <NumericTextField
      {...restProps}
      inputProps={{
        ...inputProps,
        min,
        max,
        step
      }}
      isReadonly={isReadonly}
      isDisabled={isDisabled}
      type="number"
      trailingDecoration={
        numericDecorator && <NumericDecorator
          isDisabled={isReadonly || isDisabled}
          onUpKey={onUpKey}
          onDownKey={onDownKey}
                            >{trailingDecoration}</NumericDecorator>}
      inputRef={internalRef}
    />
  );
};

export const StyledNumberField = asStyledComponent(NumberField);

export default NumberField;
